import _flatMapInstanceProperty from "@babel/runtime-corejs3/core-js/instance/flat-map";
import _mapInstanceProperty from "@babel/runtime-corejs3/core-js/instance/map";
import _sortInstanceProperty from "@babel/runtime-corejs3/core-js/instance/sort";
import PropTypes from "prop-types";
import React, { Children } from "react";
import isSourceNode from "../../utilities/isSourceNode/index.js";
import isImgNode from "../../utilities/isImgNode/index.js";
import { descendingOrderSourceCompare } from "../../utilities/compareFunctions/index.js";
import { mutexMediaQueryFactory } from "../../utilities/createMediaQueriesFromProps/index.js";

const Picture = props => {
  var _context;

  const {
    pixelDensities: parentPixelDensities,
    elementProps,
    children,
    ...rest
  } = props;
  const createMutexMediaQuery = mutexMediaQueryFactory(props);
  const {
    lazy,
    preload,
    host,
    env,
    quality,
    slug,
    id
  } = rest;
  const inheritedProps = {
    lazy,
    preload,
    host,
    env,
    quality,
    slug
  }; // Only inherited if provided

  if (id) {
    inheritedProps.id = id;
  }

  const newChildren = _flatMapInstanceProperty(_context = Children.toArray(children)).call(_context, child => {
    if (isSourceNode(child)) {
      const {
        viewport,
        preload: sourcePreload,
        pixelDensity,
        __createQuery,
        ...childProps
      } = child.props;

      if (id && childProps.id) {
        throw new Error('An id was given to the `Picture` and to the `Source`. The Picture id takes precedence, but you probably did not intend to specify an id to both. For art direction, omit the id in `Picture` component.');
      } // Allow child density to override parent


      const pixelDensities = pixelDensity ? [pixelDensity] : parentPixelDensities;
      return _mapInstanceProperty(pixelDensities).call(pixelDensities, finalPixelDensity => {
        const key = `source-${finalPixelDensity}-${viewport}`;
        return /*#__PURE__*/React.cloneElement(child, {
          key,
          viewport,
          pixelDensity: finalPixelDensity,
          __createQuery: createMutexMediaQuery,
          ...childProps,
          ...inheritedProps
        });
      });
    }

    if (isImgNode(child)) {
      if (id && child.props.id) {
        throw new Error('An id was given to the `Picture` and to the `Source`. The Picture id takes precedence, but you probably did not intend to specify an id to both. For art direction, omit the id in `Picture` component.');
      }

      return /*#__PURE__*/React.cloneElement(child, { ...child.props,
        ...inheritedProps,

        /**
         * Don't preload img element source when used with picture component
         * This creates complications because it requires creating a media query that is mutually
         * exclusive from all others
         *
         * When using `Img` component under a `Picture` component, it helps to think of it as
         * a progressive enhancement strategy -- as a fallback for browsers that don't support
         * picture and source.
         *
         * If consumers want to preload the img src, they should use the `Img` component
         * by itself
         */
        preload: false
      });
    }

    return child;
  });
  /**
   * Sort children to make sure media queries are properly organized (from most specific to least specific)
   *
   * Without this step, consumers can accidentally load larger/smaller images than desired
   */


  _sortInstanceProperty(newChildren).call(newChildren, descendingOrderSourceCompare);

  return /*#__PURE__*/React.createElement("picture", elementProps, newChildren);
};

Picture.defaultProps = {
  pixelDensities: [2, 1.5, 1]
};
process.env.NODE_ENV !== "production" ? Picture.propTypes = {
  /**
   * Whether or not to preload the image. Requires setting up a way to extract the preloads. See examples above.
   */
  preload: PropTypes.bool,

  /**
   * Media API host.
   *
   * This is completely optional. The component will try and determine the appropriate host by:
   *
   * - Checking the `UNION_MEDIA_API_HOST` environment variable
   * - Falling back to the default hosts for qa and production, depending on the value of `env`
   */
  host: PropTypes.string,

  /**
   * Media API environment to use
   *
   * This is completely optional. The component will try and determine the appropriate host by:
   *
   * - Checking if `NODE_ENV` is `production`
   * - Checking the value of `UNION_MEDIA_API_ENV` or `UNION_DEPLOYMENT_ENV`
   * - Falling back to `qa`
   */
  env: PropTypes.oneOf(['qa', 'production']),

  /**
   * List of supported pixel densities
   */
  pixelDensities: PropTypes.arrayOf(PropTypes.number),

  /**
   * Source and Img elements. The component will try to sort the elements to ensure the media
   * do not conflict with eachother
   */
  children: PropTypes.node,

  /**
   * Weather or not to enable lazy loading
   */
  lazy: PropTypes.bool,

  /**
   * Set the quality parameter to all images. [See Media API's documentation](https://api-docs.eng.xogrp.com/details/production-media-api).
   */
  quality: PropTypes.string,

  /**
   * Add a slug to the image url. [See Media API's documentation](https://api-docs.eng.xogrp.com/details/production-media-api)
   */
  slug: PropTypes.string,

  /**
   * Image id
   */
  id: PropTypes.string,

  /**
   * props to pass to `picture` element
   */
  elementProps: PropTypes.shape()
} : void 0;
export default Picture;